import { JSX, Component, createSignal, createEffect, onCleanup, children } from 'solid-js';
import { Portal } from 'solid-js/web';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  content: JSX.Element | string;
  children: JSX.Element;
  position?: TooltipPosition;
  offset?: number;
  maxWidth?: number;
  disabled?: boolean;
}

export const Tooltip: Component<TooltipProps> = (props) => {
  const [open, setOpen] = createSignal(false);
  const [style, setStyle] = createSignal({ top: 0, left: 0 });

  let anchorRef!: HTMLSpanElement;
  let tooltipRef!: HTMLDivElement;
  let hoverCount = 0; // ⭐ anchor / tooltip hover 计数

  const offset = () => props.offset ?? 10;
  const position = () => props.position ?? 'top';

  const updatePosition = () => {
    if (!anchorRef || !tooltipRef) return;
    const a = anchorRef.getBoundingClientRect();
    const t = tooltipRef.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (position()) {
      case 'top':
        top = a.top - t.height - offset();
        left = a.left + a.width / 2 - t.width / 2;
        break;
      case 'bottom':
        top = a.bottom + offset();
        left = a.left + a.width / 2 - t.width / 2;
        break;
      case 'left':
        top = a.top + a.height / 2 - t.height / 2;
        left = a.left - t.width - offset();
        break;
      case 'right':
        top = a.top + a.height / 2 - t.height / 2;
        left = a.right + offset();
        break;
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    top = Math.max(8, Math.min(top, vh - t.height - 8));
    left = Math.max(8, Math.min(left, vw - t.width - 8));

    setStyle({ top, left });
  };

  const tryClose = () => {
    requestAnimationFrame(() => {
      if (hoverCount === 0) setOpen(false);
    });
  };

  // ⭐ open 副作用
  createEffect(() => {
    if (!open()) return;

    requestAnimationFrame(updatePosition);

    const onScroll = () => open() && updatePosition();
    const onMove = () => {
      if (!anchorRef) setOpen(false);
    };
    const onPointerDown = () => {
      setOpen(false);
    };

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('pointerdown', onPointerDown, true);

    onCleanup(() => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('pointerdown', onPointerDown, true);
    });
  });

  const resolved = children(() => props.children);

  return (
    <>
      <span
        ref={anchorRef}
        onMouseEnter={() => {
          if (props.disabled) return;
          hoverCount++;
          setOpen(true);
        }}
        onMouseLeave={() => {
          hoverCount--;
          tryClose();
        }}
      >
        {resolved()}
      </span>

      {open() && !props.disabled && (
        <Portal>
          <div
            ref={tooltipRef}
            class={`betslip-tooltip tooltip tooltip-${position()}`}
            style={{
              position: 'fixed',
              top: `${style().top}px`,
              left: `${style().left}px`,
              'z-index': 10000,
              'max-width': props.maxWidth ? `${props.maxWidth}px` : '280px',
            }}
            onMouseEnter={() => hoverCount++}
            onMouseLeave={() => {
              hoverCount--;
              tryClose();
            }}
          >
            <div class="tooltip-content">{props.content}</div>
            <div class="tooltip-arrow" />
          </div>
        </Portal>
      )}
    </>
  );
};
